import { useEffect, useState } from "react";
import {
  ArrowLeft, CheckCircle, XCircle, FilePdf, PaperPlaneTilt,
  ArrowClockwise, Flask, Warning, PencilSimple, Eye,
} from "@phosphor-icons/react";
import api, { fmtErr } from "../../lib/api";
import { StatusBadge } from "./LeadsTable";
import { PlaceSearch } from "../PlaceSearch";
import { AuditEditor } from "./AuditEditor";
import { ReportPreview } from "./ReportPreview";

const LEAD_STATUSES = ["new", "auditing", "pending_review", "approved", "sent", "rejected", "contacted", "won", "lost"];

const areaCls = "w-full rounded-2xl border border-ink/10 bg-white p-4 text-sm text-ink outline-none transition-colors focus:border-pine";

const SOURCE_BADGES = {
  manual: ["Manual entry", "bg-gray-200 text-gray-700"],
  google_places: ["Google Places", "bg-pine-light text-pine"],
  sample: ["SAMPLE DATA", "bg-amber-100 text-amber-800"],
};

const blankComp = () => ({ name: "", rating: "", rating_count: "", address: "", notes: "" });

const mapEdit = (audit) => ({
  place: {
    name: audit.place?.name || "",
    address: audit.place?.address || "",
    phone: audit.place?.phone || "",
    website: audit.place?.website || "",
    categories: (audit.place?.types || []).join(", "),
    rating: audit.place?.rating ?? "",
    rating_count: audit.place?.rating_count ?? "",
    photos_count: audit.place?.photos_count ?? "",
    description: audit.place?.description || audit.place?.editorial_summary || "",
    services: (audit.place?.services || []).join("\n"),
    hours: (audit.place?.hours || []).join("\n"),
  },
  competitors: [0, 1, 2].map((i) => ({
    name: audit.competitors?.[i]?.name || "",
    rating: audit.competitors?.[i]?.rating ?? "",
    rating_count: audit.competitors?.[i]?.rating_count ?? "",
    address: audit.competitors?.[i]?.address || "",
    notes: audit.competitors?.[i]?.notes || "",
  })),
  categories: audit.scorecard?.categories || [],
  executive_summary: audit.ai?.executive_summary || "",
  competitor_comparison: audit.ai?.competitor_comparison || "",
  description_rewrite: audit.ai?.description_rewrite || "",
  next_steps: (audit.ai?.next_steps || []).join("\n"),
  opportunities: audit.ai?.opportunities || [],
  before_after: audit.ai?.before_after || [],
  manual_findings: audit.manual_findings || [],
});

export const LeadDetail = ({ leadId, onBack }) => {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [settings, setSettings] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [leadStatus, setLeadStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [place, setPlace] = useState(null);
  const [placeIdInput, setPlaceIdInput] = useState("");

  const load = async () => {
    try {
      const [d, s] = await Promise.all([
        api.get(`/admin/leads/${leadId}`),
        api.get("/admin/settings"),
      ]);
      setData(d.data);
      setSettings(s.data);
      setLeadStatus(d.data.lead.status);
      setNotes(d.data.lead.notes || "");
      setEdit(d.data.audit ? mapEdit(d.data.audit) : null);
    } catch (e) {
      setError(fmtErr(e));
    }
  };

  useEffect(() => { load(); }, [leadId]);

  const act = async (key, fn, msg) => {
    setBusy(key);
    setError("");
    setNotice("");
    try {
      await fn();
      setNotice(msg);
      await load();
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  const buildPayload = () => ({
    place: {
      name: edit.place.name,
      address: edit.place.address,
      phone: edit.place.phone,
      website: edit.place.website,
      categories: edit.place.categories.split(",").map((s) => s.trim()).filter(Boolean),
      rating: edit.place.rating === "" ? null : Number(edit.place.rating),
      rating_count: edit.place.rating_count === "" ? null : Number(edit.place.rating_count),
      photos_count: edit.place.photos_count === "" ? null : Number(edit.place.photos_count),
      description: edit.place.description,
      services: edit.place.services.split("\n").map((s) => s.trim()).filter(Boolean),
      hours: edit.place.hours.split("\n").map((s) => s.trim()).filter(Boolean),
    },
    competitors: edit.competitors.map((c) => ({
      name: c.name,
      rating: c.rating === "" || c.rating === null ? null : Number(c.rating),
      rating_count: c.rating_count === "" || c.rating_count === null ? null : Number(c.rating_count),
      address: c.address || "",
      notes: c.notes || "",
    })),
    scorecard_categories: edit.categories.map((c) => ({
      key: c.key, points: Number(c.points) || 0, max: Number(c.max) || 1, status: c.status, note: c.note || "",
    })),
    executive_summary: edit.executive_summary,
    competitor_comparison: edit.competitor_comparison,
    description_rewrite: edit.description_rewrite,
    next_steps: edit.next_steps.split("\n").map((s) => s.trim()).filter(Boolean),
    opportunities: edit.opportunities,
    before_after: edit.before_after,
    manual_findings: edit.manual_findings,
  });

  const saveEdits = () =>
    act("save", async () => {
      await api.put(`/admin/leads/${leadId}`, { status: leadStatus, notes });
      if (data.audit && edit) {
        await api.put(`/admin/audits/${data.audit.id}`, buildPayload());
      }
    }, "Saved.");

  const runApiAudit = (force = false) => {
    if (!window.confirm("This calls Google Places (and AI if enabled in Settings) — roughly $0.10–$0.20 per audit. Continue?")) return;
    act("run", async () => {
      await api.post(`/admin/leads/${leadId}/audit`, {
        place_id: place?.place_id || placeIdInput.trim() || undefined,
        force,
      });
      setTimeout(load, 5000);
    }, "API audit started — it takes about 30 seconds. Refresh to see it.");
  };

  if (!data) {
    return <p className="text-sm text-soot" data-testid="lead-detail-loading">Loading…</p>;
  }

  const { lead, audit } = data;
  const srcBadge = audit ? SOURCE_BADGES[audit.data_source] || SOURCE_BADGES.manual : null;

  return (
    <div data-testid="lead-detail">
      <button
        data-testid="lead-back-button"
        onClick={onBack}
        className="mb-6 flex items-center gap-1.5 text-sm font-semibold text-soot transition-colors hover:text-pine"
      >
        <ArrowLeft size={16} weight="bold" /> Back to leads
      </button>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-ink">{lead.business_name}</h2>
        <StatusBadge status={lead.status} />
        {srcBadge && (
          <span data-testid="source-badge" className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${srcBadge[1]}`}>
            {srcBadge[0]}
          </span>
        )}
      </div>

      {error && <p data-testid="lead-detail-error" role="alert" className="mb-4 rounded-2xl bg-red-50 p-4 text-sm font-medium text-red-700">{error}</p>}
      {notice && <p data-testid="lead-detail-notice" className="mb-4 rounded-2xl bg-pine-light p-4 text-sm font-medium text-pine">{notice}</p>}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="card-surface h-fit p-6 lg:col-span-1">
          <h3 className="font-heading text-lg font-bold text-ink">Lead</h3>
          <dl className="mt-4 space-y-2.5 text-sm">
            <div><dt className="text-soot">Contact</dt><dd className="font-semibold text-ink">{lead.contact_name || "—"}</dd></div>
            <div><dt className="text-soot">Email</dt><dd className="font-semibold text-ink">{lead.email || "—"}</dd></div>
            {lead.phone && <div><dt className="text-soot">Phone</dt><dd className="font-semibold text-ink">{lead.phone}</dd></div>}
            <div><dt className="text-soot">Listing</dt><dd className="break-all text-xs text-soot">{lead.listing_url || lead.place_id || "—"}</dd></div>
            <div><dt className="text-soot">Source</dt><dd className="text-ink">{lead.source === "manual-entry" ? "Entered by you" : "Website form"}</dd></div>
            <div><dt className="text-soot">Received</dt><dd className="text-ink">{new Date(lead.created_at).toLocaleString()}</dd></div>
          </dl>
          {lead.error && <p className="mt-4 rounded-xl bg-red-50 p-3 text-xs text-red-700">{lead.error}</p>}
          <div className="mt-5">
            <label className="field-label" htmlFor="lead-status">Pipeline status</label>
            <select
              id="lead-status"
              data-testid="lead-status-select"
              value={leadStatus}
              onChange={(e) => setLeadStatus(e.target.value)}
              className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-sm text-ink outline-none focus:border-pine"
            >
              {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div className="mt-4">
            <label className="field-label" htmlFor="lead-notes">Follow-up notes</label>
            <textarea
              id="lead-notes"
              data-testid="lead-notes-input"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`${areaCls} mt-1`}
              placeholder="Calls, follow-ups, context…"
            />
          </div>
        </div>

        <div className="space-y-6 lg:col-span-2">
          {audit && edit ? (
            <>
              {audit.data_source === "sample" && (
                <p data-testid="sample-data-banner" className="flex items-center gap-2 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm font-medium text-amber-800">
                  <Flask size={20} weight="duotone" />
                  SAMPLE DATA — this audit was generated without a live Google Places key.
                </p>
              )}
              {audit.ai?.note && (
                <p className="flex items-center gap-2 rounded-2xl border border-ink/10 bg-cream p-4 text-sm font-medium text-soot">
                  <Warning size={18} weight="duotone" className="text-amber-600" />
                  {audit.ai.note}
                </p>
              )}

              <AuditEditor edit={edit} setEdit={setEdit} dataSource={audit.data_source} />

              <div className="card-surface p-6">
                <h3 className="font-heading text-lg font-bold text-ink">Report actions</h3>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button data-testid="save-edits-button" disabled={!!busy} onClick={saveEdits} className="flex items-center gap-1.5 rounded-full border border-pine px-5 py-2.5 text-sm font-bold text-pine transition-colors hover:bg-pine-light disabled:opacity-50">
                    <PencilSimple size={15} weight="bold" /> {busy === "save" ? "Saving…" : "Save edits"}
                  </button>
                  <button data-testid="preview-report-button" disabled={!!busy} onClick={() => setPreviewOpen(true)} className="flex items-center gap-1.5 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-bold text-ink transition-colors hover:bg-cream disabled:opacity-50">
                    <Eye size={16} weight="duotone" /> Preview report
                  </button>
                  {audit.status !== "approved" && audit.status !== "sent" && (
                    <>
                      <button data-testid="approve-button" disabled={!!busy} onClick={() => act("approve", async () => { await api.put(`/admin/audits/${audit.id}`, buildPayload()); await api.post(`/admin/audits/${audit.id}/approve`); }, "Approved — PDF generated. No paid APIs were used.")} className="flex items-center gap-1.5 rounded-full bg-pine px-5 py-2.5 text-sm font-bold text-cream transition-colors hover:bg-pine-hover disabled:opacity-50">
                        <CheckCircle size={16} weight="bold" /> {busy === "approve" ? "Approving…" : "Approve & generate PDF"}
                      </button>
                      <button data-testid="reject-button" disabled={!!busy} onClick={() => act("reject", () => api.post(`/admin/audits/${audit.id}/reject`), "Rejected.")} className="flex items-center gap-1.5 rounded-full border border-red-300 px-5 py-2.5 text-sm font-bold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50">
                        <XCircle size={16} weight="bold" /> Reject
                      </button>
                    </>
                  )}
                  {audit.pdf_exists && (
                    <a data-testid="download-pdf-link" href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/audits/${audit.id}/pdf`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-bold text-ink transition-colors hover:bg-cream">
                      <FilePdf size={16} weight="duotone" /> View PDF
                    </a>
                  )}
                  {(audit.status === "approved" || audit.status === "sent") && (
                    <>
                      <button data-testid="regenerate-button" disabled={!!busy} onClick={() => act("regen", async () => { await api.put(`/admin/audits/${audit.id}`, buildPayload()); await api.post(`/admin/audits/${audit.id}/regenerate`); }, "PDF regenerated with your latest edits.")} className="flex items-center gap-1.5 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-bold text-ink transition-colors hover:bg-cream disabled:opacity-50">
                        <ArrowClockwise size={15} weight="bold" /> {busy === "regen" ? "Working…" : "Regenerate PDF"}
                      </button>
                      <button data-testid="send-report-button" disabled={!!busy || audit.status === "sent" || !lead.email} onClick={() => { if (window.confirm("This emails the customer via the email service, which uses Emergent credits. Send now?")) act("send", () => api.post(`/admin/audits/${audit.id}/send`), "Report emailed to the customer."); }} className="flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-bold text-cream transition-colors hover:bg-soot disabled:opacity-50">
                        <PaperPlaneTilt size={16} weight="bold" /> {audit.status === "sent" ? "Sent" : busy === "send" ? "Sending…" : "Email report to customer"}
                      </button>
                    </>
                  )}
                  <button
                    data-testid="rerun-audit-button"
                    disabled={!!busy || !settings?.places_enabled}
                    title={settings?.places_enabled ? "Replace this audit with fresh Google Places data" : "Enable Google Places in Settings first"}
                    onClick={() => runApiAudit(true)}
                    className="flex items-center gap-1.5 rounded-full border border-amber-300 px-5 py-2.5 text-sm font-bold text-amber-700 transition-colors hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ArrowClockwise size={15} weight="bold" /> Re-run API audit (paid)
                  </button>
                </div>
                {!settings?.places_enabled && (
                  <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-soot">
                    <Warning size={14} weight="duotone" className="text-amber-600" />
                    Google Places is disabled in Settings — API audits and re-runs stay off until you enable them there.
                  </p>
                )}
                <p className="mt-3 text-xs text-soot">
                  Audit status: <span className="font-bold text-ink">{audit.status.replace(/_/g, " ")}</span>
                  {audit.sent_at && ` · sent ${new Date(audit.sent_at).toLocaleString()}`}
                </p>
              </div>
            </>
          ) : lead.status === "auditing" ? (
            <div className="card-surface p-10 text-center">
              <p className="text-soot">Audit is being generated — this takes about 30 seconds.</p>
              <button data-testid="reload-detail-button" onClick={load} className="btn-primary mt-5 !py-2.5 text-sm">
                Refresh
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="card-surface p-6" data-testid="manual-audit-panel">
                <h3 className="font-heading text-lg font-bold text-ink">Create manual audit — free</h3>
                <p className="mt-2 text-sm text-soot">
                  Creates a blank audit you fill in yourself (or import from ChatGPT).
                  No Google Places or AI calls are made.
                </p>
                <button
                  data-testid="create-manual-audit-button"
                  disabled={!!busy}
                  onClick={() => act("manual", () => api.post(`/admin/leads/${leadId}/audit/manual`), "Blank audit created — start editing below.")}
                  className="btn-primary mt-5 disabled:opacity-50"
                >
                  {busy === "manual" ? "Creating…" : "Create blank audit"}
                </button>
              </div>

              <div className="card-surface p-6" data-testid="run-audit-panel">
                <h3 className="font-heading text-lg font-bold text-ink">Run API audit — paid</h3>
                <p className="mt-2 flex items-start gap-1.5 text-sm text-soot">
                  <Warning size={18} weight="duotone" className="mt-0.5 shrink-0 text-amber-600" />
                  This calls Google Places (and AI if enabled) — roughly $0.10–$0.20 per audit.
                  If you leave the search empty, the system resolves the customer's Maps link automatically.
                  {lead.status === "failed" && (
                    <span className="mt-1 block font-semibold text-red-600">
                      The previous attempt failed — see the error on the left.
                    </span>
                  )}
                </p>
                <div className="mt-4">
                  <PlaceSearch
                    selected={place}
                    onSelect={(p) => setPlace(p)}
                    onClear={() => setPlace(null)}
                  />
                </div>
                {!place && (
                  <input
                    data-testid="place-id-input"
                    value={placeIdInput}
                    onChange={(e) => setPlaceIdInput(e.target.value)}
                    className="mt-3 w-full rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-sm text-ink outline-none focus:border-pine"
                    placeholder="Or paste a Google Place ID (optional)"
                  />
                )}
                <button
                  data-testid="run-audit-button"
                  disabled={!!busy || !settings?.places_enabled}
                  onClick={() => runApiAudit(false)}
                  className="btn-primary mt-5 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {busy === "run" ? "Starting…" : "Run API audit"}
                </button>
                {!settings?.places_enabled && (
                  <p className="mt-3 text-xs font-medium text-soot">
                    Google Places is currently disabled — enable it in the Settings tab to use this.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {previewOpen && edit && (
        <ReportPreview lead={lead} edit={edit} settings={settings} onClose={() => setPreviewOpen(false)} />
      )}
    </div>
  );
};
